import React, { useMemo, useState, useContext } from 'react';
import { getLocale, useIntl } from 'umi';

import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import {
  formatExeclCommon,
  formatNum,
  getChartDateFormatString,
  getRatioText,
  handleDownload} from '@/pages/dashboard/utils/utils';

import CardBox from '@/pages/dashboard/components/CardBox';
import { addChartLabel, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { useLocal, useOption, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { useData, useLtvLineChartCoMemo } from './hooks';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import './index.scss';
import { ILtvLineChartProps } from '@/pages/dashboard/pages/monetization/ltv/components/LTVLineChartCo/type';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import CommonBubble from '@home/components/CommonBubble';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import { getName } from '@home/utils/common';
import { resolveAndSelectName } from '@/pages/dashboard/utils/metricUtils';

const hasMetricValue = (rawValue: unknown): boolean =>
  rawValue !== '' && rawValue !== null && rawValue !== undefined;

export default function LTVLineChart(props: ILtvLineChartProps) {
  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl();
  const isEn = getLocale() === 'en-US';
  const [isEye, setIsEye] = useState(true);

  const { data, style, className, config, segmentVal } = props;
  const { tag, metricsInfo } = data || {};
  const {
    title,
    titleCN,
    explainEn = '',
    explainCn = '',
    api,
    componentMetrics: propsComponentMetrics,
    isHaveSubTitle,
    enChartTooltipsName,
    cnChartTooltipsName,
    iconList = ['vs', 'eyes'],
    beforeRequest,
    formatData,
    showDollarSignInLineChartBubble = true,
    cycle: cycleConfig,
  } = config || {};
  const tooltipTitleText = isEn ? enChartTooltipsName : cnChartTooltipsName;
  const titleText = getName(isEn, title, titleCN);
  const explainText = getName(isEn, explainEn, explainCn);

  const titleCom = explainText ? (
    <CommonBubble
      dark
      placement="top"
      content={FormatHtmlTextToReact({ text: explainText, className: 'overlay-card-desc' })}
    >
      <span className='dbd-monetization-ltv-line-chart__title-com'>{titleText}</span>
    </CommonBubble>
  ) : titleText;

  const contextObj = useContext(FilterContext);
  const { filterLoading, filterVal = {} } = contextObj || {};
  const timeType = filterVal?.timeType || 'Day';
  const extraParams = useMemo(() => {
    if (segmentVal === 'aggs') {
      return { logical_flag: 'weighted_daily_average' };
    }
    return undefined;
  }, [segmentVal]);
  const ltvTrendData = useData(api, filterVal, filterLoading, tag, beforeRequest, formatData, extraParams, cycleConfig);
  const {
    componentData,
    error,
    loading,
  } = ltvTrendData;

  const {
    componentMetrics,
  } = useLtvLineChartCoMemo({
    data,
    propsComponentMetrics
  });

  const { isEN } = useLocal();

  const chartOptions = useMemo(() => {
    const chartData: Record<string, { name: string; data: Array<{ x: string; y: string; }>; }> = {};
    const registerData: Record<string, { register_dod: number; register: number; }> = {};
    const tableData: Array<Record<string, string>> = [];
    const downloadData: Array<Record<string, string>> = [];
    let hasAnyMetricData = false;

    componentMetrics?.forEach(metric => {
      if (hasMetricValue(componentData?.[metric.avgResKey])) {
        hasAnyMetricData = true;
      }
    });

    componentMetrics?.forEach(metric => {
      chartData[metric?.nodeKey] = {
        name: (isEN ? metric.enName : metric.cnName) || '',
        data: [],
      };
    });

    componentData?.Nodes?.forEach((node: Record<string, unknown>) => {
      const {
        date: lDate = '',
      } = node || {};

      const date = String(lDate);

      const tableRow: Record<string, string> = { date };
      const downloadRow: Record<string, string> = { date };

      componentMetrics?.forEach((metric) => {
        const rawValue = node?.[metric?.nodeKey];
        const hasValue = hasMetricValue(rawValue);
        if (hasValue) {
          hasAnyMetricData = true;
        }
        const value = hasValue ? String(rawValue) : undefined;
        chartData[metric?.nodeKey]?.data?.push({ x: date, y: value });
        tableRow[metric?.nodeKey] = hasValue
          ? String(formatNum(value))
          : '-';
        downloadRow[metric?.nodeKey] = hasValue
          ? formatExeclCommon(value) || ''
          : '';
      });

      tableData.push(tableRow);
      downloadData.push(downloadRow);

      registerData[date] = {
        register_dod: Number(node.register_dod),
        register: Number(node.register),
      };
    });

    const tableColumns: {
      key: string;
      align: string;
      width: number;
      header: string;
      render?: (cvm: Record<string, unknown>) => React.ReactNode;
    }[] = [
      {
        key: 'date',
        align: 'left',
        width: 100,
        header: formatMessage({ id: 'monetization_ltv_Date' }),
        render: (cvm: any) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
            {timeType !== 'Month' && timeType !== 'Week' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      }
    ];

    const chartDataArr: any[] = [];

    const sortedMetricsForChart = [...(componentMetrics || [])].sort(
      (a, b) => {
        const aHasData = chartData[a.nodeKey]?.data?.some(item =>
          hasMetricValue(item?.y),
        );
        const bHasData = chartData[b.nodeKey]?.data?.some(item =>
          hasMetricValue(item?.y),
        );
        return Number(bHasData) - Number(aHasData);
      },
    );

    sortedMetricsForChart.forEach(metric => {
      chartDataArr.push(chartData[metric?.nodeKey]);
    });

    componentMetrics?.forEach(metric => {
      tableColumns.push({
        key: metric.nodeKey,
        align: 'right',
        width: 100,
        header: (isEN ? metric.enName : metric.cnName),
      });
    });

    return {
      chart: chartDataArr,
      table: {
        option: tableColumns,
        data: tableData,
        downloadData,
      },
      registerData,
      hasAnyMetricData,
    };
  }, [componentData, getLocale(), timeType, componentMetrics, isEN]);

  const handleAxisFormat = (value: any) => getChartDateFormatString(timeType, value);

  const lineConfig = {
    line: {
      formatterFunc: (params: Array<{ seriesName?: string; value?: unknown; color?: string; }>) => {
        const paramsByName = new Map(
          (params || []).map(p => [p.seriesName, p]),
        );
        const data = (componentMetrics || []).map(metric => {
          const name = (isEN ? metric.enName : metric.cnName) || '';
          const param = paramsByName.get(name);
          const value = param?.value;
          const hasValue =
            value !== '' && value !== null && value !== undefined;
          const metricsNum = hasValue
            ? `${showDollarSignInLineChartBubble ? '$' : ''}${formatNum(value)}`
            : '-';
          return {
            name,
            metricsNum,
            ratioNum: '',
            color: param?.color,
          };
        });
        const { text, status } = getRatioText(chartOptions.registerData[params[0].axisValue]?.register_dod);
        const sumNum = formatNum(chartOptions?.registerData?.[params?.[0]?.axisValue]?.register, false, true);
        return getEchartTooltipText({
          title: params[0]?.axisValue,
          sumNum,
          sumName: tooltipTitleText,
          sumRatio: text,
          hasSum: true,
          ratioStatus: status,
          hasRatio: true,
          data,
        });
      },
      setOtherConfigFunc: (opt) => {
        // opt.yAxis.axisLabel.formatter = (value) => {
        //   const num = value < 1 && value > 0 ? value.toFixed(3) : formatNum(value, false, true)
        //   return num
        // }
        opt.xAxis[0].axisLabel.formatter = (value) => getChartDateFormatString(timeType, value);
        return opt;
      },
    },
  };
  const { xAxisData, legendData, lineSeries } = useOption(chartOptions);
  const {hasAnyMetricData} = chartOptions;
  const showChart =
    hasAnyMetricData &&
    ((chartOptions?.table?.data?.length ?? 0) > 0 || xAxisData.length > 0);

  const renderList = [
    {
      type: 'eyes',
      svgType: isEye ? 'open-eyes' : 'eyes-closed',
      content: isEye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
      isHideIcon: !iconList?.includes('eyes'),
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      isHideIcon: !iconList?.includes('lineChart'),
      component: <LineChart
        height={310}
        legendData={showChart ? legendData : []}
        xAxisData={showChart ? xAxisData : []}
        series={showChart ? lineSeries : []}
        formatterFunc={lineConfig?.line?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (lineConfig?.line?.setOtherConfigFunc) opt = lineConfig?.line?.setOtherConfigFunc(opt);
          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; }) => {
              const result = formatNum(params?.value, false);
              return result;
            },
            show: isEye,
            formatNum: (value: string | number) => {
              return formatNum(value, false);
            }
          });
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => {
              return handleAxisFormat(params?.value);
            },
          });
          return opt;
        }}
      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      isHideIcon: !iconList?.includes('sheet'),
      component: <MiniTable
        height={310}
        loading={false}
        tableConfig={lineConfig?.table}
        tableOption={chartOptions?.table?.option}
        tableList={chartOptions?.table?.data}
      />
    },
  ];


  const subTitleCom = useMemo(() => {
    const timeType = filterVal?.timeType || 'Day';
    return (
      <div className='register-table-time' style={{ marginBottom: 10 }}>
        {
          (loading || filterLoading || error || !hasAnyMetricData) ?
            <div style={{ height: 21 }}></div> :
            <>{
              componentMetrics?.map(((item: any, index: number) => {
                // 只有 segment 选中 aggs 且有 rangeNameByGranularity 配置时才使用
                const useGranularityConfig = segmentVal === 'aggs' && item?.rangeNameByGranularity;
                const granularityConfig = useGranularityConfig ? (item?.rangeNameByGranularity?.[timeType?.toLowerCase()] || {}) : {};
                const rawRangeEnName = granularityConfig.rangeEnName ?? item?.rangeEnName;
                const rawRangeCnName = granularityConfig.rangeCnName ?? item?.rangeCnName;
                const rangeName = metricsInfo
                  ? resolveAndSelectName(isEn, rawRangeEnName, rawRangeCnName, metricsInfo) || (isEn ? rawRangeEnName : rawRangeCnName) || ''
                  : (isEn ? rawRangeEnName : rawRangeCnName) || '';
                const avgValue = componentData?.[item?.avgResKey];
                const hasAvgValue = hasMetricValue(avgValue);
                return (
                  <span
                    key={`${item?.avgResKey}_${index}`}
                    className='time-text'
                  >
                    {rangeName} :{' '}
                    {hasAvgValue
                      ? (item.preUnit ?? '') +
                        dataFormat({
                          value: avgValue,
                          formatRule: item?.formatter,
                        })
                      : '-'}
                  </span>
                );
              }))
            }
            </>
        }
      </div>
    );
  }, [loading, filterLoading, error, hasAnyMetricData, componentMetrics, componentData, isEn, filterVal?.timeType, metricsInfo, segmentVal]);

  return (
    <div
      style={style}
      className={`dbd-monetization-ltv-line-chart ${className}`}
    >
      <CardBox
        className="register-table"
        height={340}
        bordered={false}
        titleCom={titleCom}
        renderList={renderList || []}
        defaultAvtive={1}
        setCurrentDataType={(type: any) => {
          if (type === 'eyes') {
            setIsEye(!isEye);
          }
        }}
        subTitleCom={isHaveSubTitle ? subTitleCom : ''}
        downloadClick={async () => {
          handleDownload(chartOptions?.table, title);
        }}
        isLoading={loading}
        isError={error}
        isNodata={!hasAnyMetricData}
      />
    </div>
  );
}
